import React, { useState, useEffect, useCallback } from "react";
import ImageZoom from "react-image-zoom-rotate";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Button,
} from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
import { withStyles } from "@material-ui/core/styles";
import { ArrowBack, ArrowForward, Flag } from "@material-ui/icons";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const ColorButton = withStyles((theme) => ({
  root: {
    color: theme.palette.getContrastText(grey[900]),
    backgroundColor: grey[900],
    "&:hover": {
      backgroundColor: grey[900],
    },
    height: 50,
    minWidth: 100,
  },
}))(Button);

function ViewAnnotation(props) {
  const { benchmarkId, imageKey: encodedImageKey } = useParams();
  const initialImageKey = decodeURIComponent(encodedImageKey);

  const [image, setImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [annotation, setAnnotation] = useState([]);
  const [ocr, setOcr] = useState([]);
  const [flag, setFlag] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [currentImageKey, setCurrentImageKey] = useState(initialImageKey);
  const [benchmarkDetails, setBenchmarkDetails] = useState(props.location.state || {
    benchmark: benchmarkId,
    id: '',
    name: '',
    benchmarkType: '',
    ocrType: '',
    projectType: '',
    annotationList: []
  });

  const handleDelete = () => {
    const imageId = currentImageKey.split('/').pop().split('.')[0];
    axios
      .post("/api/benchmarks/annotation/delete", {
        benchmark: benchmarkId,
        imageId,
      })
      .then((res) => {
        handleBack();
      })
      .catch((e) => {
        enqueueSnackbar(e.response.data, {
          variant: "error",
        });
      });
  };

  const loadData = (imgKey = currentImageKey) => {
    const imageId = imgKey.split('/').pop().split('.')[0];
    setLoading(true);
    axios
      .post("/api/benchmarks/annotation/view", {
        benchmark: benchmarkId,
        imageId,
        imageKey: imgKey,
      })
      .then((res) => {
        setLoading(false);
        setImage(res.data.image);
        setAnnotation(res.data.annotationFileBody);
        setOcr(res.data.ocr);
        if (res.data.flags && res.data.flags[imageId] && res.data.flags[imageId].annotation) {
          setFlag(res.data.flags[imageId].annotation);
        } else {
          setFlag(false);
        }
      })
      .catch((e) => {
        setLoading(false);
        enqueueSnackbar('Error', {
          variant: "error",
        });
      });
  };

  const getDetails = async () => {
    try {
      const res = await axios.post('/api/benchmarks/status', { benchmark: benchmarkId });
      const imagesRes = await axios.post("/api/benchmarks/images/list", { benchmarkId });
      const annotList = [];
      imagesRes.data.images.forEach((obj) => {
        if (obj.annotation) {
          annotList.push(obj.imageKey);
        }
      });

      setBenchmarkDetails({
        benchmark: benchmarkId,
        id: res.data.id,
        name: res.data.name,
        benchmarkType: res.data.benchmarkType,
        ocrType: res.data.ocrType,
        projectType: res.data.projectType,
        annotationList: annotList
      });
    } catch (e) {
      console.error("Error fetching benchmark details", e);
    }
  };

  const handleEdit = () => {
    const imageId = currentImageKey.split('/').pop().split('.')[0];
    props.history.push({
      pathname: `/benchmark/annotate/${benchmarkId}/${encodeURIComponent(currentImageKey)}`,
      state: {
        ...benchmarkDetails,
        imageId,
        imageKey: currentImageKey,
        type: "edit",
      },
    });
  };

  const handlePrevious = () => {
    const { annotationList } = benchmarkDetails;
    if (!annotationList || annotationList.length === 0) return;
    const currentIndex = annotationList.indexOf(currentImageKey);
    let nextIndex = currentIndex - 1;
    if (nextIndex < 0) {
      nextIndex = annotationList.length - 1;
    }
    const nextKey = annotationList[nextIndex];
    setCurrentImageKey(nextKey);
    props.history.replace(`/benchmark/viewAnnotation/${benchmarkId}/${encodeURIComponent(nextKey)}`);
  };

  const handleNext = () => {
    const { annotationList } = benchmarkDetails;
    if (!annotationList || annotationList.length === 0) return;
    const currentIndex = annotationList.indexOf(currentImageKey);
    let nextIndex = currentIndex + 1;
    if (nextIndex > annotationList.length - 1) {
      nextIndex = 0;
    }
    const nextKey = annotationList[nextIndex];
    setCurrentImageKey(nextKey);
    props.history.replace(`/benchmark/viewAnnotation/${benchmarkId}/${encodeURIComponent(nextKey)}`);
  };

  const handleFlag = () => {
    const imageId = currentImageKey.split('/').pop().split('.')[0];
    axios
      .post("/api/benchmarks/flags/flag", {
        benchmark: benchmarkId,
        imageId,
        value: !flag,
        type: 'annotation',
      })
      .then((res) => {
        loadData();
      })
      .catch((e) => {
        enqueueSnackbar('Error', {
          variant: "error",
        });
      });
  };


  const handleKeyNavigation = useCallback((event) => {
    if (!loading) {
      if (event.keyCode === 37) {
        handlePrevious();
      }
      if (event.keyCode === 39) {
        handleNext();
      }
    }
  }, []);

  const handleHome = () => {
    props.history.push('/');
  };

  const handleBack = () => {
    props.history.push({
      pathname: `/benchmark/${benchmarkId}`,
      state: benchmarkDetails,
    });
  }

  useEffect(() => {
    loadData();
    if (!benchmarkDetails.id) {
      getDetails();
    }
  }, [currentImageKey]);

  return (
    <div style={{ paddingLeft: 10, paddingRight: 10 }}>
      <ColorButton
        onClick={handleHome}
        variant="contained"
        color="primary"
        style={{ float: "right", marginRight: "90px" }}
      >
        Home
      </ColorButton>
      <ColorButton
        onClick={handleBack}
        variant="contained"
        color="primary"
        style={{ float: "left", marginRight: "90px" }}
      >
        Go Back
      </ColorButton>
      <br></br>
      <h1>Image: {currentImageKey.split('/').pop()}</h1>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <ImageZoom
            rotate={(clockwise) => {
              return (
                <div>
                  <Button
                    onClick={clockwise}
                    style={{ position: "absolute", right: 0, zIndex: 1 }}
                  >
                    ↻
                  </Button>
                </div>
              );
            }}
            src={`data:image/png;base64,${image}`}
            width={650}
            zoomWidth={500}
          />
        </Grid>
        <Grid item xs={6}>
          {benchmarkDetails.benchmarkType === "group" ? (
            <TableContainer>
              <Table aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">GT</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <StyledTableRow>
                    <StyledTableCell align="center">
                      {annotation.e2e_gt}
                    </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              {benchmarkDetails.projectType !== 'validation' && <>
                <TableContainer>
                  <Table aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Labels</StyledTableCell>
                        <StyledTableCell align="center">e2e_gt</StyledTableCell>
                        <StyledTableCell align="center">ocr_gt</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {annotation.length === 0
                        ? ""
                        : Object.keys(annotation.e2e_gt).map((row, i) => (
                          <StyledTableRow key={row.name}>
                            <StyledTableCell component="th" scope="row">
                              {row}
                            </StyledTableCell>
                            <StyledTableCell align='center'>
                              {annotation.e2e_gt[row]}
                            </StyledTableCell>
                            <StyledTableCell align='center'>
                              {annotation.ocr_gt[row]}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer></>}
              {benchmarkDetails.projectType !== 'extraction' && <>
                <h1>Validation</h1>
                <TableContainer>
                  <Table aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Labels</StyledTableCell>
                        <StyledTableCell align="center">validation</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {annotation.length === 0
                        ? ""
                        : Object.keys(annotation.validationAnnotation).map((key, index) => (
                          <StyledTableRow key={key}>
                            <StyledTableCell component="th" scope="row">
                              {key}
                            </StyledTableCell>
                            <StyledTableCell align='center'>
                              {annotation.validationAnnotation[key]}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>}
            </>
          )}
          <br></br>
          <br></br>
          <Button
            onClick={() => handleEdit()}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Reannotate
          </Button>{" "}
          <Button
            onClick={() => handleDelete()}
            variant="contained"
            color="secondary"
            disabled={loading}
          >
            Delete
          </Button>{" "}
          <Button
            onClick={handlePrevious}
            variant="contained"
            color="black"
            disabled={loading}
            startIcon={<ArrowBack />}
          >
            Previous
          </Button>{" "}
          <Button
            onClick={handleNext}
            variant="contained"
            color="black"
            disabled={loading}
            endIcon={<ArrowForward />}
          >
            Next
          </Button>{" "}
          <Button
            onClick={handleFlag}
            variant="contained"
            color="secondary"
            disabled={loading}
            endIcon={<Flag />}
          >
            {flag ? "Unflag" : "Flag"}
          </Button>
        </Grid>
      </Grid>
      <div style={{ paddingLeft: 30 }}>
        <h1 style={{ fontSize: 20 }}>OCR Output</h1>
        {ocr.map(function (line, i) {
          return (
            <p style={{ margin: 0 }}>
              {line}
              <br></br>
            </p>
          );
        })}
      </div>
      <br></br>
      <br></br>
    </div>
  );
}

export default ViewAnnotation;
